import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CreditCard, MapPin, AlertCircle, Wallet, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import Layout from '@/components/layout/Layout';
import apiService from '@/services/api';

interface QuoteData {
    id: number;
    service_name: string;
    total_amount_pence: number;
    approval_status: string;
    payment_status?: string;
    customer_id: number;
    created_at: string;
}

interface WalletBalance {
    balance: number;
    currency: string;
}

export default function QuotePaymentPage() {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const quoteId = parseInt(id || '0', 10);

    const [quote, setQuote] = useState<QuoteData | null>(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [walletBalance, setWalletBalance] = useState<WalletBalance | null>(null);

    const [formData, setFormData] = useState({
        paymentMethod: 'card'
    });

    useEffect(() => {
        loadQuote();
        loadWalletBalance();
        
        // Check if returning from Stripe payment
        const params = new URLSearchParams(window.location.search);
        const paymentStatus = params.get('payment');
        const sessionId = sessionStorage.getItem(`stripe_session_${quoteId}`);
        
        console.log(`🔍 QuotePaymentPage loaded for quote #${quoteId}`);
        console.log(`   URL payment status: ${paymentStatus}`);
        console.log(`   sessionId from storage: ${sessionId ? sessionId.substring(0, 20) + '...' : 'NOT FOUND'}`);
        
        if (paymentStatus === 'success' && sessionId) {
            console.log('✅ Payment success detected, verifying with backend...');
            
            // Verify payment with backend
            (async () => {
                try {
                    // Wait for Stripe to fully process the session
                    console.log('⏳ Waiting for Stripe to process (2s)...');
                    await new Promise(resolve => setTimeout(resolve, 2000));
                    
                    console.log(`📤 Calling verification endpoint with quoteId=${quoteId}, sessionId=${sessionId.substring(0, 20)}...`);
                    
                    // Call backend to verify and complete payment
                    const response = await apiService.request('/quotes/payment/verify-session', 'POST', {
                        quoteId,
                        sessionId
                    });
                    
                    console.log('✅ Verification endpoint response:', response);
                    
                    if (response.completed) {
                        console.log('✅ Payment verified and completed!');
                        setSuccess(true);
                        // Reload quote to show completed status
                        const updatedQuote = await apiService.request(`/quotes/${quoteId}`, 'GET');
                        if (updatedQuote.data) {
                            console.log('✅ Quote reloaded:', updatedQuote.data);
                            setQuote(updatedQuote.data);
                        }
                    } else {
                        console.log('⏳ Payment verification returned not completed:', response);
                        // Poll a few more times if not completed
                        let attempts = 0;
                        let paymentCompleted = false;
                        while (attempts < 5 && !paymentCompleted) {
                            console.log(`   Retry ${attempts + 1}/5 after 2s...`);
                            await new Promise(resolve => setTimeout(resolve, 2000));
                            const retryResponse = await apiService.request(`/quotes/${quoteId}`, 'GET');
                            console.log(`   Quote status check:`, retryResponse.data?.payment_status);
                            if (retryResponse.data?.payment_status === 'completed') {
                                console.log('✅ Quote status updated to completed!');
                                setQuote(retryResponse.data);
                                setSuccess(true);
                                paymentCompleted = true;
                                break;
                            }
                            attempts++;
                        }
                        if (!paymentCompleted) {
                            console.warn('⚠️ Payment still not showing as completed after retries, but loading quote anyway');
                            const fallback = await apiService.request(`/quotes/${quoteId}`, 'GET');
                            if (fallback.data) {
                                setQuote(fallback.data);
                                if (fallback.data.payment_status === 'completed') {
                                    setSuccess(true);
                                }
                            }
                        }
                    }
                } catch (error) {
                    console.error('❌ Error verifying payment:', error);
                    // Still try to load quote status anyway
                    try {
                        const fallback = await apiService.request(`/quotes/${quoteId}`, 'GET');
                        if (fallback.data) {
                            console.log('📊 Fallback quote load:', fallback.data);
                            setQuote(fallback.data);
                            if (fallback.data.payment_status === 'completed') {
                                setSuccess(true);
                            }
                        }
                    } catch (e) {
                        // Silently fail
                    }
                }
            })();
            
            return;
        } else if (paymentStatus === 'success' && !sessionId) {
            // Page returned with success but no session ID stored
            // This might happen if browser storage was cleared or cookies expired
            console.warn('⚠️ Payment success indicated but no sessionId found. Checking quote status directly...');
            (async () => {
                try {
                    const quoteLookup = await apiService.request(`/quotes/${quoteId}`, 'GET');
                    console.log(`   Quote status from direct lookup:`, quoteLookup.data?.payment_status);
                    if (quoteLookup.data?.payment_status === 'completed') {
                        setQuote(quoteLookup.data);
                        setSuccess(true);
                        console.log('✅ Quote status is completed!');
                    }
                } catch (e) {
                    console.error('   Error checking quote status:', e);
                }
            })();
        }
    }, [quoteId]);

    const loadQuote = async () => {
        try {
            const response = await apiService.request(`/quotes/${quoteId}`, 'GET');
            if (response.data) {
                setQuote(response.data);
                console.log('Quote loaded:', response.data.payment_status);
                
                // FALLBACK: If quote shows pending payment but we have a sessionId stored,
                // attempt verification automatically
                if (response.data.payment_status === 'pending') {
                    const sessionId = sessionStorage.getItem(`stripe_session_${quoteId}`);
                    if (sessionId) {
                        console.log('⚠️ Quote is pending but sessionId exists in storage. Attempting automatic verification...');
                        try {
                            const verifyResponse = await apiService.request('/quotes/payment/verify-session', 'POST', {
                                quoteId,
                                sessionId
                            });
                            console.log('Auto-verification response:', verifyResponse);
                            if (verifyResponse.data?.completed) {
                                console.log('✅ Auto-verification succeeded!');
                                // Reload quote
                                const updated = await apiService.request(`/quotes/${quoteId}`, 'GET');
                                if (updated.data) {
                                    setQuote(updated.data);
                                }
                            }
                        } catch (e) {
                            console.log('Auto-verification attempt failed (non-critical):', e.message);
                        }
                    }
                }
            }
        } catch (error) {
            console.error('Failed to load quote:', error);
            setError('Failed to load quote details');
        } finally {
            setLoading(false);
        }
    };

    const loadWalletBalance = async () => {
        try {
            const data = await apiService.request('/wallet', 'GET');
            if (data.data && data.data.wallet) {
                const wallet = data.data.wallet;
                setWalletBalance({
                    balance: (wallet.balance_pence || wallet.balance || 0) / 100, // Convert pence to pounds
                    currency: wallet.currency || 'GBP'
                });
            }
        } catch (error) {
            console.error('Failed to load wallet balance:', error);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSubmitting(true);

        try {
            if (!quote) {
                throw new Error('Quote not found');
            }

            const amountGBP = quote.total_amount_pence / 100;

            // Validate wallet payment
            if (formData.paymentMethod === 'wallet' && walletBalance) {
                if (walletBalance.balance < amountGBP) {
                    setError('Insufficient wallet balance');
                    setSubmitting(false);
                    return;
                }
            }

            // Process payment based on method
            const paymentData = {
                quoteId,
                amount: quote.total_amount_pence,
                paymentMethod: formData.paymentMethod,
                amountGBP
            };

            let response;

            if (formData.paymentMethod === 'card') {
                // Initiate Stripe payment
                console.log('💳 Initiating payment with data:', paymentData);
                response = await apiService.request('/quotes/payment/initiate', 'POST', paymentData);
                
                console.log('💳 Payment initiation response:', response);
                
                // Check for duplicate payment error
                if (response.alreadyPaid) {
                    setError('This quote has already been paid. Only one payment is allowed per quote.');
                    setSubmitting(false);
                    // Reload quote to show current status
                    const updatedQuote = await apiService.request(`/quotes/${quoteId}`, 'GET');
                    if (updatedQuote.data) {
                        setQuote(updatedQuote.data);
                    }
                    return;
                }
                
                if (response.data && response.data.sessionUrl && response.data.sessionId) {
                    console.log('✅ Payment session created, storing:', response.data.sessionId);
                    console.log('🔗 Redirecting to:', response.data.sessionUrl);
                    // Store session ID for verification after payment
                    sessionStorage.setItem(`stripe_session_${quoteId}`, response.data.sessionId);
                    // Redirect to Stripe checkout
                    window.location.href = response.data.sessionUrl;
                } else {
                    console.error('❌ Invalid response structure:', response);
                    throw new Error(response.message || response.error || 'Failed to initiate payment');
                }
            } else if (formData.paymentMethod === 'wallet') {
                // Process wallet payment
                response = await apiService.request(`/quotes/${quoteId}/pay-with-wallet`, 'POST', { amount: quote.total_amount_pence });
                
                if (response.success) {
                    setSuccess(true);
                    setTimeout(() => {
                        navigate(`/dashboard/quotes/${quoteId}`);
                    }, 2000);
                } else {
                    throw new Error(response.message || 'Wallet payment failed');
                }
            } else if (formData.paymentMethod === 'bank_transfer') {
                // Mark quote as awaiting bank transfer
                response = await apiService.request(`/quotes/${quoteId}/payment-method`, 'PUT', { 
                    method: 'bank_transfer'
                });
                
                if (response.success) {
                    setSuccess(true);
                    setTimeout(() => {
                        navigate(`/dashboard/quotes/${quoteId}`);
                    }, 2000);
                } else {
                    throw new Error(response.message || 'Failed to set payment method');
                }
            }
        } catch (err) {
            console.error('Payment error:', err);
            setError(err instanceof Error ? err.message : 'Payment processing failed');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <section className="section-padding bg-background">
                    <div className="container-custom">
                        <div className="text-center py-12">
                            <p className="text-gray-600">Loading quote details...</p>
                        </div>
                    </div>
                </section>
            </Layout>
        );
    }

    if (!quote) {
        return (
            <Layout>
                <section className="section-padding bg-background">
                    <div className="container-custom">
                        <div className="text-center py-12">
                            <AlertCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
                            <h1 className="text-2xl font-bold text-gray-900 mb-2">Quote Not Found</h1>
                            <p className="text-gray-600 mb-6">The quote you're looking for doesn't exist.</p>
                            <Button onClick={() => navigate('/dashboard')} variant="outline">
                                Back to Dashboard
                            </Button>
                        </div>
                    </div>
                </section>
            </Layout>
        );
    }

    // Check if payment is already completed
    if (quote.payment_status === 'completed') {
        return (
            <Layout>
                <section className="section-padding bg-background">
                    <div className="container-custom">
                        <div className="text-center py-12">
                            <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto mb-4" />
                            <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Already Completed</h1>
                            <p className="text-gray-600 mb-2">Quote #{ quote.id} has already been paid.</p>
                            <p className="text-gray-600 mb-6">Only one payment is allowed per quote.</p>
                            <Button onClick={() => navigate(`/dashboard/quotes/${quoteId}`)} variant="outline">
                                Back to Quote Details
                            </Button>
                        </div>
                    </div>
                </section>
            </Layout>
        );
    }

    const amountGBP = (quote.total_amount_pence / 100).toFixed(2);
    const canPayWithWallet = walletBalance && walletBalance.balance >= parseFloat(amountGBP);

    return (
        <Layout>
            <Helmet>
                <title>{quote && quote.id ? `Payment - Quote #${quote.id} | Virewire` : 'Payment | Virewire'}</title>
                <meta name="description" content={quote && quote.id ? `Complete payment for quote #${quote.id}` : 'Quote Payment'} />
            </Helmet>

            <section className="section-padding bg-background">
                <div className="container-custom">
                    {/* Success Message */}
                    {success && (
                        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                            <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
                            <p className="text-green-800">Payment processed successfully! Redirecting...</p>
                        </div>
                    )}

                    {/* Back Button */}
                    <Button 
                        variant="ghost" 
                        onClick={() => navigate(`/dashboard/quotes/${quoteId}`)} 
                        className="mb-6"
                    >
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Back to Quote
                    </Button>

                    <div className="grid md:grid-cols-3 gap-8">
                        {/* Payment Form */}
                        <div className="md:col-span-2">
                            <Card className="shadow-card">
                                <CardHeader>
                                    <CardTitle className="text-2xl">Payment for {quote.service_name}</CardTitle>
                                    <p className="text-gray-600 mt-2">Quote #{quote.id}</p>
                                </CardHeader>
                                <CardContent>
                                    <form onSubmit={handleSubmit} className="space-y-6">
                                        {/* Error Message */}
                                        {error && (
                                            <div className="bg-red-50 border border-red-200 rounded p-4 flex gap-3">
                                                <AlertCircle className="text-red-600 flex-shrink-0 h-5 w-5" />
                                                <p className="text-red-800">{error}</p>
                                            </div>
                                        )}

                                        {/* Payment Method Section */}
                                        <div>
                                            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                                <CreditCard size={20} />
                                                Select Payment Method
                                            </h2>

                                            {/* Wallet Balance Display */}
                                            {walletBalance && walletBalance.balance > 0 && (
                                                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                                    <div className="flex items-center gap-2">
                                                        <Wallet size={18} className="text-blue-600" />
                                                        <span className="text-blue-900">
                                                            Wallet Balance: <strong>£{(walletBalance.balance / 100).toFixed(2)}</strong>
                                                        </span>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Payment Method Options */}
                                            <div className="space-y-3">
                                                {/* Credit/Debit Card */}
                                                <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all" 
                                                       style={{
                                                           borderColor: formData.paymentMethod === 'card' ? '#2563eb' : '#d1d5db',
                                                           backgroundColor: formData.paymentMethod === 'card' ? '#eff6ff' : 'white'
                                                       }}>
                                                    <input
                                                        type="radio"
                                                        name="paymentMethod"
                                                        value="card"
                                                        checked={formData.paymentMethod === 'card'}
                                                        onChange={handleChange}
                                                        className="w-4 h-4 text-blue-600 cursor-pointer"
                                                    />
                                                    <div className="ml-3">
                                                        <p className="font-medium text-gray-900">Credit/Debit Card</p>
                                                        <p className="text-sm text-gray-600">Pay securely with Visa, Mastercard, or American Express</p>
                                                    </div>
                                                </label>

                                                {/* Wallet Payment */}
                                                {walletBalance && walletBalance.balance > 0 && (
                                                    <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all" 
                                                           style={{
                                                               borderColor: formData.paymentMethod === 'wallet' ? '#2563eb' : (canPayWithWallet ? '#d1d5db' : '#fecaca'),
                                                               backgroundColor: formData.paymentMethod === 'wallet' ? '#eff6ff' : 'white',
                                                               opacity: canPayWithWallet ? 1 : 0.6
                                                           }}>
                                                        <input
                                                            type="radio"
                                                            name="paymentMethod"
                                                            value="wallet"
                                                            checked={formData.paymentMethod === 'wallet'}
                                                            onChange={handleChange}
                                                            disabled={!canPayWithWallet}
                                                            className="w-4 h-4 text-blue-600 cursor-pointer"
                                                        />
                                                        <div className="ml-3">
                                                            <p className="font-medium text-gray-900">Wallet Payment</p>
                                                            <p className="text-sm text-gray-600">
                                                                Pay from your wallet balance
                                                                {!canPayWithWallet && (
                                                                    <span className="ml-2 text-red-600">Insufficient balance</span>
                                                                )}
                                                            </p>
                                                        </div>
                                                    </label>
                                                )}

                                                {/* Bank Transfer */}
                                                <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all" 
                                                       style={{
                                                           borderColor: formData.paymentMethod === 'bank_transfer' ? '#2563eb' : '#d1d5db',
                                                           backgroundColor: formData.paymentMethod === 'bank_transfer' ? '#eff6ff' : 'white'
                                                       }}>
                                                    <input
                                                        type="radio"
                                                        name="paymentMethod"
                                                        value="bank_transfer"
                                                        checked={formData.paymentMethod === 'bank_transfer'}
                                                        onChange={handleChange}
                                                        className="w-4 h-4 text-blue-600 cursor-pointer"
                                                    />
                                                    <div className="ml-3">
                                                        <p className="font-medium text-gray-900">Bank Transfer</p>
                                                        <p className="text-sm text-gray-600">Transfer funds directly to our business account</p>
                                                    </div>
                                                </label>
                                            </div>
                                        </div>

                                        {/* Submit Button */}
                                        <button
                                            type="submit"
                                            disabled={submitting || success}
                                            className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {submitting ? 'Processing Payment...' : `Pay £${amountGBP}`}
                                        </button>

                                        <p className="text-xs text-gray-500 text-center">
                                            Your payment is processed securely using industry-standard encryption
                                        </p>
                                    </form>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Payment Summary */}
                        <div className="md:col-span-1">
                            <Card className="shadow-card sticky top-4">
                                <CardHeader>
                                    <CardTitle>Payment Summary</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div>
                                        <p className="text-sm text-gray-600 mb-1">Service</p>
                                        <p className="font-semibold text-gray-900">{quote.service_name}</p>
                                    </div>

                                    <div>
                                        <p className="text-sm text-gray-600 mb-1">Quote ID</p>
                                        <p className="font-semibold text-gray-900">#{quote.id}</p>
                                    </div>

                                    <div>
                                        <p className="text-sm text-gray-600 mb-1">Quote Date</p>
                                        <p className="font-semibold text-gray-900">
                                            {new Date(quote.created_at).toLocaleDateString('en-GB')}
                                        </p>
                                    </div>

                                    <div className="border-t pt-4">
                                        <div className="flex justify-between mb-2">
                                            <span className="text-gray-600">Amount</span>
                                            <span className="font-semibold">£{amountGBP}</span>
                                        </div>
                                        <div className="flex justify-between text-lg font-bold text-gray-900 border-t pt-2">
                                            <span>Total Due</span>
                                            <span className="text-blue-600">£{amountGBP}</span>
                                        </div>
                                    </div>

                                    {walletBalance && (
                                        <div className="bg-gray-50 p-3 rounded-lg mt-4">
                                            <p className="text-xs text-gray-600 mb-1">Wallet Balance</p>
                                            <p className="font-semibold text-gray-900">£{(walletBalance.balance / 100).toFixed(2)}</p>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </div>
            </section>
        </Layout>
    );
}
